import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import { Button, Form, InputNumeric, Loading, Switch, Tooltip, Icons, Select } from '@gpstrackit/gtc-react-controls';
import { Card, CardContent, CardHeader } from '@mui/material';
import { NotificationManager } from 'Components/Notifications';
import { updateInterventionConfig, resetIntervention, getRiskEvents, searchAssetProfiles, setProfileId } from './redux/slice';
import './style.scss';

// Helper function to get translated event name
const getTranslatedEventName = (eventName, messages) => {
    const key = `event_${eventName?.replace(/\s+/g, '_')?.toLowerCase()}`;
    return messages[key] || eventName;
};

// Helper function to get translated event category
const getTranslatedEventCategory = (eventCategory, messages) => {
    const key = `eventCategory_${eventCategory?.toLowerCase()}`;
    return messages[key] || eventCategory;
};

const InterventionWidget = ({ configData, allConfigs }) => {
    const dispatch = useDispatch();
    const { messages } = useIntl();
    const formRef = Form.useForm();
    const hasLoadedRef = useRef(false);

    const { update, loadingUpdate, riskEvents, loadingRiskEvents, nextToken, totalRiskEvents, profileId, profiles } = useSelector(state => state.interventionRedux || {});

    const [data, setData] = useState({ isEnabled: false });
    const [assignedEvents, setAssignedEvents] = useState([]);
    const [isEditable, setIsEditable] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (data.isEnabled && !hasLoadedRef.current) {
            dispatch(searchAssetProfiles());
            hasLoadedRef.current = true;
        }
    }, [data.isEnabled, dispatch]);

    useEffect(() => {
        if (profileId && data.isEnabled) {
            dispatch(getRiskEvents({ profileId }));
        }
    }, [profileId, data.isEnabled, dispatch]);

    // Load more events when scrolling
    const loadMoreEvents = () => {
        if (nextToken && !loadingRiskEvents && profileId) {
            dispatch(getRiskEvents({ profileId, loadMore: true }));
        }
    };

    // Handle scroll event for infinite loading
    const handleScroll = (e) => {
        const { scrollTop, scrollHeight, clientHeight } = e.target;
        if (scrollHeight - scrollTop <= clientHeight + 50) {
            loadMoreEvents();
        }
    };

    // Get available events (not assigned)
    const availableEvents = useMemo(() => {
        const assignedIds = assignedEvents.map(e => e.eventId);
        return riskEvents.filter(e => !assignedIds.includes(e.eventId));
    }, [riskEvents, assignedEvents]);

    useEffect(() => {
        if (configData) {
            setData({
                isEnabled: configData.configEnable === 1
            });

            if (configData.jsonValues) {
                try {
                    const config = JSON.parse(configData.jsonValues);
                    setAssignedEvents(config.riskEvents || []);
                } catch (error) {
                    console.error('Error parsing intervention config:', error);
                    setAssignedEvents([]);
                }
            } else {
                setAssignedEvents([]);
            }
        }
    }, [configData]);



    const validateFields = () => {
        if (!data?.isEnabled) return true;
        return assignedEvents.length > 0;
    };

    const handleSave = async () => {
        if (!validateFields()) {
            NotificationManager.error(
                messages['driverPerformanceFunctionTab_common_error'],
                messages['driverPerformanceFunctionTab_intervention_interventionEventRequired'],
                3000
            );
            return;
        }

        const payload = {
            config: {
                isEnabled: data?.isEnabled || false,
                riskEvents: assignedEvents
            }
        };

        setIsSubmitting(true);
        try {
            await dispatch(updateInterventionConfig(payload));
        } finally {
            setIsSubmitting(false);
        }
    };

    const addEvent = (event) => {
        let defaultSeverity = "50";
        try {
            if (event.severityParameters && event.severityParameters !== '{}') {
                const params = JSON.parse(event.severityParameters);
                defaultSeverity = params.threshold?.toString() || "50";
            }
        } catch (error) {
            console.warn('Error parsing severityParameters:', error);
        }

        const newAssigned = [...assignedEvents, {
            eventId: event.eventId,
            eventName: event.eventName,
            eventCategory: event.eventCategory,
            severity: defaultSeverity
        }];
        setAssignedEvents(newAssigned);
        setIsEditable(true);
    };

    const removeEvent = (eventId) => {
        setAssignedEvents(assignedEvents.filter(e => e.eventId !== eventId));
        setIsEditable(true);
    };

    const updateSeverity = (eventId, severity) => {
        setAssignedEvents(assignedEvents.map(e =>
            e.eventId === eventId ? { ...e, severity: severity.toString() } : e
        ));
        setIsEditable(true);
    };

    const handleProfileChange = (selectedOption) => {
        if (selectedOption && selectedOption.value && selectedOption.value !== profileId) {
            dispatch(setProfileId(selectedOption.value));
        }
    };

    const selectedProfile = useMemo(() => {
        return profiles.find(p => p.id === profileId);
    }, [profiles, profileId]);

    const handleToggleChange = (value) => {
        setData({ ...data, isEnabled: value });
        setIsEditable(true);
    };

    useEffect(() => {
        if (update) {
            if (update?.success) {
                NotificationManager.success(
                    messages['driverPerformanceFunctionTab_common_success'],
                    messages['driverPerformanceFunctionTab_intervention_updatedConfiguration'],
                    3000
                );
                setIsEditable(false);
            } else {
                const errorMessage = update?.error || messages['driverPerformanceFunctionTab_intervention_updatedConfigurationError'];
                NotificationManager.error(
                    messages['driverPerformanceFunctionTab_common_error'],
                    errorMessage,
                    5000
                );
            }
            dispatch(resetIntervention({ update: null }));
        }
    }, [update, messages, dispatch]);

    return (
        <Card className="widget-paper" style={{ position: 'relative' }}>
            {(loadingUpdate || isSubmitting || loadingRiskEvents) && <Loading overlay show style={{ position: 'absolute' }} />}
            <CardHeader
                title={
                    <div className="in-header-content">
                        <div className="header-title">
                            {messages['driverPerformanceFunctionTab_intervention_intervention']}
                            <Tooltip
                                className="function-panel-tooltip"
                                placement="bottom"
                                title={messages['driverPerformanceFunctionTab_intervention_severityTooltip'] || 'Severity range: 1-100'}>
                                <Icons name="help" size="small"/>
                            </Tooltip>
                        </div>
                        <div className="header-row">
                            <Switch
                                checked={data?.isEnabled || false}
                                label={messages['driverPerformanceFunctionTab_intervention_enableIntervention']}
                                labelPosition="left"
                                onChange={handleToggleChange}
                                size="small"
                            />
                        </div>
                    </div>
                }
            />
            <CardContent>
                <Form
                    ref={formRef.ref}
                    loading={loadingUpdate || isSubmitting}
                    onValidSubmit={async () => {
                        await handleSave();
                    }}
                    className="intervention_widget"
                >
                    <div className="dual-panel-container">
                        {/* Available Events Section */}
                        <div className="function-panel-section">
                            <div className="function-panel-section-header-with-select">
                                <h4 className="function-panel-section-header">{messages['driverPerformanceFunctionTab_intervention_riskEvent']}</h4>
                                {profiles.length > 0 && (
                                    <div className="profile-selector-inline">
                                        <Select
                                            value={selectedProfile ? { value: selectedProfile.id, label: selectedProfile.name } : null}
                                            onChange={handleProfileChange}
                                            options={profiles.map(p => ({ value: p.id, label: p.name }))}
                                            placeholder={messages['driverPerformanceFunctionTab_intervention_selectProfile'] || 'Select Profile'}
                                            isDisabled={!data?.isEnabled}
                                        />
                                    </div>
                                )}
                            </div>

                            <div className="events-list" onScroll={handleScroll}>
                                <div className="list-header">
                                    <span>{messages['driverPerformanceFunctionTab_intervention_category']}</span>
                                    <span>{messages['driverPerformanceFunctionTab_intervention_eventName']}</span>
                                    <span></span>
                                </div>
                                {availableEvents.map(event => (
                                    <div key={event.eventId} className="event-item">
                                        <span className="event-category">{getTranslatedEventCategory(event.eventCategory, messages)}</span>
                                        <span className="event-name">{getTranslatedEventName(event.eventName, messages)}</span>
                                        <Button
                                            variant="secondary"
                                            size="small"
                                            onClick={() => addEvent(event)}
                                            isDisabled={!data?.isEnabled}
                                            className="add-btn"
                                        >
                                            +
                                        </Button>
                                    </div>
                                ))}
                                {loadingRiskEvents && (
                                    <div className="loading-more">
                                        <Loading show size="small" />
                                    </div>
                                )}
                                {!nextToken && riskEvents.length > 0 && (
                                    <div className="no-more-items">
                                        {messages['driverPerformanceFunctionTab_intervention_eventsLoaded']?.replace('{loaded}', availableEvents.length)?.replace('{total}', totalRiskEvents)}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Assigned Events Section */}
                        <div className="function-panel-section">
                            <h4 className="function-panel-section-header">
                                {messages['driverPerformanceFunctionTab_intervention_assignedEvents']}
                            </h4>

                            <div className="assigned-events-list">
                                <div className="list-header">
                                    <span>{messages['driverPerformanceFunctionTab_intervention_eventName']}</span>
                                    <span>{messages['driverPerformanceFunctionTab_intervention_severity']}</span>
                                    <span></span>
                                </div>
                                {assignedEvents.map(event => (
                                    <div key={event.eventId} className="assigned-event-item">
                                        <span className="event-name">{getTranslatedEventName(event.eventName, messages)}</span>
                                        <div className="metric-input">
                                            <div className="severity-input-wrapper">
                                                <InputNumeric
                                                    value={parseInt(event.severity) || 1}
                                                    onChange={(value) => {
                                                        if (value === '' || value === null || value === undefined) {
                                                            updateSeverity(event.eventId, '');
                                                            return;
                                                        }
                                                        const numValue = parseInt(value);
                                                        if (!isNaN(numValue) && numValue >= 1 && numValue <= 100) {
                                                            updateSeverity(event.eventId, numValue);
                                                        }
                                                    }}
                                                    onBlur={() => {
                                                        const currentValue = parseInt(event.severity);
                                                        if (!event.severity || event.severity === '' || isNaN(currentValue)) {
                                                            updateSeverity(event.eventId, 1);
                                                        }
                                                    }}
                                                    min={1}
                                                    max={100}
                                                    isInteger={true}
                                                    isDisabled={!data?.isEnabled}
                                                    className="severity-input"
                                                />
                                                <div className="severity-arrows">
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            const newValue = Math.min(100, (parseInt(event.severity) || 1) + 1);
                                                            updateSeverity(event.eventId, newValue);
                                                        }}
                                                        className="arrow-button"
                                                        disabled={!data?.isEnabled}
                                                    >
                                                        ▲
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            const newValue = Math.max(1, (parseInt(event.severity) || 1) - 1);
                                                            updateSeverity(event.eventId, newValue);
                                                        }}
                                                        className="arrow-button"
                                                        disabled={!data?.isEnabled}
                                                    >
                                                        ▼
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                        <Button
                                            variant="secondary"
                                            size="small"
                                            onClick={() => removeEvent(event.eventId)}
                                            isDisabled={!data?.isEnabled}
                                            className="remove-btn"
                                        >
                                            ×
                                        </Button>
                                    </div>
                                ))}
                                {assignedEvents.length === 0 && (
                                    <div className="empty-state">
                                        <p>{messages['driverPerformanceFunctionTab_intervention_noEventsAssigned']}</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {data?.isEnabled && assignedEvents.length === 0 && (
                        <div className="function-panel-note">
                            {messages['driverPerformanceFunctionTab_intervention_interventionEventRequired']}
                        </div>
                    )}

                    <div className="function-panel-footer">
                        <Button
                            variant="primary"
                            onClick={() => formRef?.submit()}
                            isDisabled={!isEditable || (data?.isEnabled && assignedEvents.length === 0)}
                        >
                            {messages['driverPerformanceFunctionTab_common_save']}
                        </Button>
                    </div>
                </Form>
            </CardContent>
        </Card>
    );
};

export default InterventionWidget;
